// Real 3D units & buildings for the WebGL view. Each entity is a low-poly mesh
// (models3d/*) sharing one flat-shaded material; geometries are cached and shared
// across identical entities. Pure view: reads GameState + domain, mutates only its
// own scene objects. Owner: client-render3d.

import {
  BackSide, BufferAttribute, BufferGeometry, CircleGeometry, DoubleSide, Group, LineBasicMaterial,
  LineLoop, Material, Mesh, MeshBasicMaterial, MeshLambertMaterial, type Object3D, Plane, Scene, Vector3,
} from 'three'
import { CHUNK, DIRS, chunkKey, hexDist, hexToPixel, lerp } from '../../shared/hex'
import { BUILDINGS, HEX_CAP, NPC_COLOR, WILD_COLOR, buildingFootprint, isBoxOrder, isTerraform } from '../../shared/constants'
import { unitCombat, unitSpeed } from '../../shared/person'
import { Terrain, moveCost } from '../../shared/terrain'
import { buildingStorage, townHallPhase } from '../../shared/townhall'
import { HEX_SIZE, type GameState } from '../state'
import { citizenLook } from '../render/entities'
import { sitePrepPhase } from '../render/terrain'
import { treeGrowthStage } from '../render/treeGrowth'
import { tileHeight } from './terrainMesh'
import { RACK_ITEMS, buildingGeometry, rackItemGeometry, rackSlotLocal, sitePileGeometry, stakesGeometry } from './models3d/buildings3d'
import { HAND, HIP_X, HIP_Y, SHOULDER_X, SHOULDER_Y, cargoGeometry, heldTool, serfRig, toolArmScale, unitGeometry } from './models3d/units3d'
import type { Building, UnitWire } from '../../shared/types'
import { avoidObstacles, separation, type Walker, type Obstacle } from './avoidance'
import { FarmVisual } from './farms3d'
import { FARM_SOIL_Y } from './models3d/farm3d'

const S = HEX_SIZE
const HEX_RAD = Math.PI / 3
const TURN_SPEED = 4.2 // rad/s a unit pivots toward its travel direction (~0.75s for a half turn)
const FACE_SPEED_EPS = 0.1 // world units/s; ignore resting drift without losing slow or high-FPS turns

// ---- serf walk cycle -------------------------------------------------------------
const LEG_LEN = 7 // hip→foot, for tying stride length to swing (no foot skating)
const LEG_SWING = 0.523 // rad, peak leg swing fore/aft (~30°)
const ARM_SWING = 0.384 // rad, free-arm swing (~22°); the tool arm scales this down
// One full stride (2π of phase) must carry the body exactly its stride length so the
// planted foot doesn't slide: stride = 4·legLen·sin(swing). Phase advances per world
// unit moved, so cadence tracks real ground speed regardless of the 1 Hz sim.
const STRIDE_FREQ = (2 * Math.PI) / (4 * LEG_LEN * Math.sin(LEG_SWING))
const BOB = 0.6 // world units the body dips at double-support (down-only → no waist gap)
const LEAN = 0.105 // rad, forward lean into travel (~6°)
const SWAY = 0.052 // rad, side-to-side body roll per step (~3°)
const GAIT_RISE = 8 // walk amplitude fades in fast when setting off…
const GAIT_FALL = 2.5 // …and settles gently on a stop, so brief pauses don't snap limbs
const MOVE_EPS = 0.02 // per-frame world distance above which a unit counts as "walking"
const IDLE_BOB = 0.13 // gentle breathing rise while standing
const IDLE_FREQ = 1.8 // breaths per second (× a per-unit phase seed so a crowd desyncs)
const CARRY_ARM = 0.95 // rad; both arms held forward to cradle carried cargo in front

// ---- melee closing ----------------------------------------------------------------
// Adjacent hex centres are ~42 world units apart — melee fought from there reads as
// swinging at thin air. Fighters visually close toward their victim (both sides meet
// near the shared edge) and glide back when the fight ends. Pure view; range rules
// are untouched, and ranged attackers (range > 1) keep their distance.
const CLOSE_MARGIN = 4 // stop this far past the halfway line toward the target
const CLOSE_MIN = 12 // never stand closer than this to the target's centre
const CLOSE_MAX = 18 // cap on the visual displacement (world units)
const CLOSE_RISE = 4 // how fast the closing eases in (per second)…
const CLOSE_FALL = 5 // …and releases when the fight ends

/** Final on-screen world position per unit, written by EntityLayer3d each frame so
 *  overlays (rings, HP bars) track the same point as the mesh — including melee
 *  closing and hop smoothing. */
export type UnitVis = { x: number; y: number; z: number }

// ---- attack strikes ---------------------------------------------------------------
// A strike fires whenever the server bumps a unit's attack cooldown (one per blow).
const SWING_DUR = 0.45 // s per swing/bite
const FARM_SWING_DUR = 0.8 // s per hoe stroke — slower, heavier than a combat chop
const FARM_SWING_EVERY = 1.9 // s between strokes while working the rows
const FARM_SOIL_LIFT = FARM_SOIL_Y // farmhands stand on the modeled soil surface
/** Serf weapon-arm angle over a strike: raise behind the shoulder, chop through, recover. */
function strikeArm(t: number): number {
  if (t < 0.3) return -1.7 * (t / 0.3)
  if (t < 0.55) return -1.7 + 2.6 * ((t - 0.3) / 0.25)
  return 0.9 * (1 - (t - 0.55) / 0.45)
}
/** Sharp-attack envelope 0→1→0 (fast in, slower out) for lunges/leans/bites. */
function strikePulse(t: number): number {
  return t < 0.25 ? t / 0.25 : 1 - (t - 0.25) / 0.75
}
const GAIT_GRACE = 0.4 // s a unit keeps its walk cycle through brief stalls/hop resnaps
const DOME = 0.7 // terrainMesh's hex-top crown — contact blobs ride it so they don't clip
const HEX_W = Math.sqrt(3) * S // centre-to-centre spacing of adjacent hexes

/** Effective mp/tick the server advances this unit's current hop at: speed over the
 *  destination tile's move cost (mirrors stepUnits + shared moveCost). Keeping the
 *  client's extrapolation at the true rate stops walk-freeze stutter on slow/fast
 *  ground (forest, swamp, roads). */
function hopRateAt(st: GameState, u: UnitWire, q: number, r: number): number {
  const c = st.chunks.get(chunkKey(Math.floor(q / CHUNK), Math.floor(r / CHUNK)))
  if (!c) return unitSpeed(u)
  const lq = ((q % CHUNK) + CHUNK) % CHUNK
  const lr = ((r % CHUNK) + CHUNK) % CHUNK
  const i = lr * CHUNK + lq
  const cost = moveCost(c.tiles[i] as Terrain, c.roads.has(i))
  return Number.isFinite(cost) && cost > 0 ? unitSpeed(u) / cost : unitSpeed(u)
}
function hopRate(st: GameState, u: UnitWire): number {
  return hopRateAt(st, u, u.q, u.r)
}

/** Dead-reckoning bounds: keep walking at most this many known route steps past
 *  the authoritative hop during a network gap. Beyond it the unit waits — a
 *  longer outage is the catching-up chip's job, not the ghost's. */
const PRED_MAX_HOPS = 2
/** Correction glide: how fast an absorbed authority jump decays (per second). */
const CORR_EASE = 5
/** Where the hall's side workbench sits relative to the townhall anchor (must
 *  match the bench baked into the 3D model) — the crafter stands just south
 *  of it, facing the benchtop. */
const HALL_BENCH = { x: 42, z: 13 }

/** Store-fill bucket for the townhall's storage annex (0..3): the goods
 *  stacked under the lean-to grow with the hub's actual load. Quantised so
 *  the geometry key only flips on real steps; foreign halls (store wire-
 *  stripped) always read empty — contents are private anyway. */
function storeVariant(b: Building): number {
  if (b.type !== 'townhall') return 0
  let total = 0
  for (const k in b.store) total += b.store[k as keyof typeof b.store] ?? 0
  const cap = buildingStorage(b)
  if (total < 1) return 0
  if (total < cap * 0.25) return 1
  if (total < cap * 0.6) return 2
  return 3
}

/** Full townhall model variant: store bucket in bits 0-1, the front weapon
 *  rack's RACK_ITEMS mask in bits 2+. The rack mounts the OWN faction's
 *  spare armory items; other players' halls hang bare rails (their racks
 *  are as private as their stores). */
function hallVariant(st: GameState, b: Building): number {
  if (b.type !== 'townhall') return 0
  let mask = 0
  if (b.owner === st.me && st.self?.items) {
    for (let i = 0; i < RACK_ITEMS.length; i++) {
      if ((st.self.items[RACK_ITEMS[i]] ?? 0) > 0) mask |= 1 << i
    }
  }
  return storeVariant(b) | (mask << 2)
}
/** Jumps between these bounds (world px) glide; smaller are noise, larger are
 *  intentional teleports (respawn, hall exit) that should snap. */
const CORR_MIN = HEX_W * 0.3
const CORR_MAX = HEX_W * 2.5

/** Height of the crowned hex top under a unit partway (mp) through a hop — the
 *  ground rises DOME at each hex centre and falls to 0 at the shared edge. */
function crownAt(mp: number): number {
  const d = Math.min(mp, 1 - mp) * HEX_W
  return DOME * Math.max(0, 1 - d / S)
}

// Units may legally share a hex (HEX_CAP) — spread co-located units into a small
// ring (like the 2.5D slot layout) so they never render inside each other. The
// offset eases, so slot changes at hop boundaries glide instead of popping.
const SLOT_EASE = 4

// Anticipatory separation is bounded to the local hex route. Obstacles are
// resolved after combat closing as well, so that animation cannot undo clearance.
const AVOID_EASE = 8
const BULK_RAD = 22
const WALK_OVER = new Set(['road', 'ramp', 'pier', 'palisade', 'dummy'])

// ---- hover silhouette -------------------------------------------------------------
// Inverted-hull outline: a copy of the mesh displaced outward along position-welded
// normals by a constant world thickness, drawn back-face in a flat colour — a neat,
// even rim on anything from a serf to the town hall. Hulls attach as children of the
// hovered meshes, so they ride walk/strike animations for free.
const HULL_T = 0.55 // outline thickness in world units — thin, not fat
const HULL_CACHE = new WeakMap<BufferGeometry, BufferGeometry>()

function outlineGeometry(src: BufferGeometry): BufferGeometry {
  let g = HULL_CACHE.get(src)
  if (g) return g
  const arr = src.getAttribute('position').array as Float32Array
  const n = arr.length / 3
  // accumulate face normals onto position-welded vertices (flat meshes share no verts)
  const acc = new Map<string, [number, number, number]>()
  const keyOf = (i: number): string =>
    `${Math.round(arr[i * 3] * 20)},${Math.round(arr[i * 3 + 1] * 20)},${Math.round(arr[i * 3 + 2] * 20)}`
  for (let f = 0; f < n; f += 3) {
    const a = f * 3, b = (f + 1) * 3, c = (f + 2) * 3
    const ux = arr[b] - arr[a], uy = arr[b + 1] - arr[a + 1], uz = arr[b + 2] - arr[a + 2]
    const vx = arr[c] - arr[a], vy = arr[c + 1] - arr[a + 1], vz = arr[c + 2] - arr[a + 2]
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
    const l = Math.hypot(nx, ny, nz) || 1
    nx /= l; ny /= l; nz /= l
    for (const i of [f, f + 1, f + 2]) {
      const k = keyOf(i)
      const s = acc.get(k)
      if (s) { s[0] += nx; s[1] += ny; s[2] += nz } else acc.set(k, [nx, ny, nz])
    }
  }
  const out = new Float32Array(arr.length)
  for (let i = 0; i < n; i++) {
    const s = acc.get(keyOf(i))!
    const l = Math.hypot(s[0], s[1], s[2]) || 1
    out[i * 3] = arr[i * 3] + (s[0] / l) * HULL_T
    out[i * 3 + 1] = arr[i * 3 + 1] + (s[1] / l) * HULL_T
    out[i * 3 + 2] = arr[i * 3 + 2] + (s[2] / l) * HULL_T
  }
  g = new BufferGeometry()
  g.setAttribute('position', new BufferAttribute(out, 3))
  HULL_CACHE.set(src, g)
  return g
}

/** The resource a hauling serf is carrying the most of ('' if none) — picks the cargo model. */
function dominantCargo(u: UnitWire): string {
  if (u.craftHaul) return u.craftHaul // hall crafter's cosmetic material bundle
  const c = u.carrying
  if (!c) return ''
  let best = '', amt = 0
  for (const k in c) { const v = (c as Record<string, number>)[k] ?? 0; if (v > amt) { amt = v; best = k } }
  return best
}

function ownerColor(st: GameState, owner: string): string {
  return st.factions.get(owner)?.color ?? (owner === 'wild' ? WILD_COLOR : NPC_COLOR)
}
/** True beasts render as single sculpted meshes; every other unit is a humanoid
 *  that gets the articulated serf rig (walk + strikes). */
const ANIMAL_TYPES = new Set(['wolf', 'bear', 'deer'])

function unitLook(u: UnitWire): string {
  if (u.type === 'citizen') return citizenLook(u)
  switch (u.type) {
    case 'spearman': return 'spearman'
    case 'archer': return 'archer'
    case 'raider': return 'swordsman' // marauders read as sword-swinging brutes
    default: return 'idle' // legacy worker/carrier — a plain villager
  }
}

/** The articulated pieces of a walking serf. Legs hang off the ROOT (so the body's
 *  bob/lean never lifts a planted foot); the inner group carries the upper body and
 *  both arms and is what bobs/leans/sways. Null for animals (one static mesh). */
interface SerfRigInst {
  inner: Group; legL: Mesh; legR: Mesh; armL: Mesh; armR: Mesh
  tool: Mesh | null; toolRest: number
  carryPivot: Group // shoulder-hinged holder: the cargo swings exactly with the arms
  phase: number; gait: number; seed: number; toolSwing: number
  lastMove: number // clock time of the last real movement (gait grace window)
  carry: boolean // a hauler: hold the arms forward and cradle a cargo model
  cargo: Mesh | null; cargoRes: string // the currently-held load model, swapped by resource
}
interface UEnt {
  root: Object3D // the pickable, world-placed object (a Group for serfs, a Mesh for animals)
  rig: SerfRigInst | null
  blobM: Mesh // contact-shadow disc; its height rides the hex-top crown each frame
  key: string; yaw: number; vmp: number; hopKey: number
  hopQ: number; hopR: number // current hop's destination, to detect path continuations
  lastX: number; lastZ: number // last world position, for per-frame distance (gait + stride)
  // Dead reckoning past the wire's current hop (own units carry wpath): how many
  // route steps we've walked locally beyond the authoritative hop + progress
  // within the current predicted step. Reset whenever a fresh hop lands.
  predIdx: number; predMp: number
  // Eased world-px correction absorbing authority jumps (late ticks, prediction
  // misses) so units glide to the truth instead of teleporting.
  corrX: number; corrZ: number
  baseX: number; baseZ: number // last BASE (pre-slot/avoidance) position, for jump detection
  atkT: number // strike progress 0..1 (≥1 = idle)
  lastCd: number // last seen attack cooldown — an increase means a new blow landed
  nextSwing: number // clock time of the next rhythm swing while in fighting state
  closeF: number // eased 0..1 weight of the melee closing displacement
  workF: number // eased 0..1 weight of the laborer's bent-over posture (farm/build/dig)
  liftF: number // eased 0..1 weight of the farm soil-lift (farmhands only)
  swingDur: number // duration of the current swing (combat chop vs slower hoe stroke)
  slotX: number; slotZ: number // eased crowd-slot offset (units sharing a hex)
  sepX: number; sepZ: number // eased avoidance offset (unit separation + building bulk)
}
interface BEnt { root: Group; key: string; mats: Material[] }

/** Construction stage from progress: 0 = bare site, 1..3 = rising, 4 = done. */
function buildStage(done: boolean, progress: number, buildTime: number): number {
  if (done) return 4
  const pf = Math.min(1, progress / Math.max(1, buildTime))
  return pf <= 0 ? 0 : pf < 0.33 ? 1 : pf < 0.66 ? 2 : 3
}
/** Fraction of the model height standing at each stage. */
const STAGE_H = [0, 0.3, 0.62, 0.92, 1]
/** Where each resource's pile sits on the site (hex-local xz). */
const PILE_SLOT: Record<string, { x: number; z: number }> = {
  wood: { x: -12, z: 7 }, stone: { x: 12, z: 7 }, iron: { x: 0, z: 14 }, gold: { x: 0, z: 14 }, food: { x: 0, z: 14 },
}

/** Manages 3D meshes for every visible unit and building. */
export class EntityLayer3d {
  private mat = new MeshLambertMaterial({ vertexColors: true, flatShading: true, side: DoubleSide })
  private uEnts = new Map<number, UEnt>()
  private bEnts = new Map<number, BEnt>()
  private clock = 0 // wall-clock seconds, for the standing idle breath
  // Soft blob "contact shadow" under every entity — grounds things on backends
  // where the real shadow map isn't available, and reads as gentle AO where it is.
  private blobGeo = ((): CircleGeometry => { const g = new CircleGeometry(1, 14); g.rotateX(-Math.PI / 2); return g })()
  private blobMat = new MeshBasicMaterial({ color: 0x14200e, transparent: true, opacity: 0.20, depthWrite: false })
  // Footprint plates for a multi-hex construction site: a tinted hexagon laid on
  // each footprint tile so the whole plot reads on the ground — through the trees
  // still standing on it and the faint see-through overlay. Pointy-top (rotateY
  // π/6) to sit flush on the terrain hexes; a brighter inner ring gives an edge.
  private plateGeo = ((): CircleGeometry => { const g = new CircleGeometry(S * 0.94, 6); g.rotateX(-Math.PI / 2); g.rotateY(Math.PI / 6); return g })()
  private plateMat = new MeshBasicMaterial({ color: 0xe6a93a, transparent: true, opacity: 0.26, depthWrite: false, side: DoubleSide })
  private plateEdgeGeo = ((): BufferGeometry => {
    const pts: number[] = []
    for (let i = 0; i <= 6; i++) { const a = Math.PI / 6 + (i / 6) * Math.PI * 2; pts.push(Math.cos(a) * S * 0.94, 0, Math.sin(a) * S * 0.94) }
    const g = new BufferGeometry(); g.setAttribute('position', new BufferAttribute(new Float32Array(pts), 3)); return g
  })()
  private plateEdgeMat = new LineBasicMaterial({ color: 0xffd27a, transparent: true, opacity: 0.7 })
  private blob(rad: number): Mesh {
    const m = new Mesh(this.blobGeo, this.blobMat)
    m.position.y = 0.14
    m.scale.set(rad, 1, rad)
    return m
  }

  /** Amber footprint plates for a multi-hex site: one flat hexagon (with a
   *  brighter edge) laid on each tile the finished building will cover, so the
   *  whole plot reads on the ground even under standing trees. The group cancels
   *  the root's rotation so plates stay flush with the world hex grid. */
  private footprintPlates(st: GameState, b: Building, rootRotY: number): Group | null {
    const cells = buildingFootprint(b.type, b.q, b.r, b.dir)
    if (cells.length < 2) return null
    const g = new Group()
    g.rotation.y = -rootRotY
    const a = hexToPixel(b.q, b.r, S)
    const ay = tileHeight(st, b.q, b.r)
    for (const c of cells) {
      const p = hexToPixel(c.q, c.r, S)
      const y = tileHeight(st, c.q, c.r) - ay + DOME + 0.3
      const plate = new Mesh(this.plateGeo, this.plateMat)
      plate.position.set(p.x - a.x, y, p.y - a.y)
      g.add(plate)
      const edge = new LineLoop(this.plateEdgeGeo, this.plateEdgeMat)
      edge.position.set(p.x - a.x, y + 0.05, p.y - a.y)
      g.add(edge)
    }
    return g
  }

  /** unitAnim: shared per-unit final world position, written here each frame so
   *  overlays/HUD (selection rings, HP bars) track the exact point the mesh occupies
   *  (hop smoothing and melee closing included). */
  constructor(private scene: Scene, private unitAnim: Map<number, UnitVis>) {}

  sync(st: GameState, dt: number): void {
    this.syncBuildings(st)
    this.syncFarms(st, dt)
    this.syncUnits(st, dt)
    this.syncHover(st)
  }

  // hover silhouette state
  private hovKey = ''
  private hulls: Mesh[] = []
  private hullMat = new MeshBasicMaterial({ color: 0xcdeaff, side: BackSide })

  private reducedFarmMotion = typeof matchMedia !== 'undefined' ? matchMedia('(prefers-reduced-motion: reduce)') : null
  private syncFarms(st: GameState, dt: number): void {
    for (const [id, ent] of this.bEnts) {
      const farm = ent.root.userData.farm as FarmVisual | undefined
      const b = farm && st.buildings.get(id)
      if (farm && b && farm.update(st, b, dt, this.clock, this.reducedFarmMotion?.matches ?? false)) {
        const h = st.uiHover ?? st.hoverEnt
        if (h?.kind === 'building' && h.id === id) this.hovKey = ''
      }
    }
  }

  /** Attach/refresh the outline hulls on whatever entity the cursor is over —
   *  or on the building a panel chip is pointing at (st.uiHover, which wins:
   *  the cursor's own hover is stale while the pointer sits on the panel). */
  private syncHover(st: GameState): void {
    const h = st.uiHover ?? st.hoverEnt
    const root = h ? (h.kind === 'unit' ? this.uEnts.get(h.id)?.root : this.bEnts.get(h.id)?.root) : undefined
    const key = h && root ? `${h.kind}:${h.id}:${root.uuid}` : ''
    if (key === this.hovKey) return
    this.hovKey = key
    for (const m of this.hulls) m.parent?.remove(m)
    this.hulls.length = 0
    if (!root) return
    const targets: Mesh[] = []
    root.traverse((o: Object3D) => {
      const m = o as Mesh
      // skip the blob, other hulls, and clip-staged site meshes (their hull would
      // silhouette the FULL model around a half-built site)
      const mat = m.material as Material | undefined
      if (m.isMesh && mat !== this.blobMat && mat !== this.hullMat && !(mat && (mat.clippingPlanes?.length ?? 0) > 0)) targets.push(m)
    })
    for (const m of targets) {
      const hull = new Mesh(outlineGeometry(m.geometry), this.hullMat)
      hull.raycast = () => {} // never eats picks
      m.add(hull)
      this.hulls.push(hull)
    }
  }

  /** Dev/testing: the live object for an entity (for screen projection). */
  unitMesh(id: number): Object3D | undefined { return this.uEnts.get(id)?.root }
  buildingMesh(id: number): Object3D | undefined { return this.bEnts.get(id)?.root }
  /** Dev/testing: the live rig (limb transforms) for a serf, or null for animals. */
  unitRig(id: number): SerfRigInst | null | undefined { return this.uEnts.get(id)?.rig }
  /** Dev/testing: smoothed-motion internals for a unit. */
  unitDbg(id: number): { vmp: number; hopKey: number } | undefined {
    const e = this.uEnts.get(id)
    return e ? { vmp: e.vmp, hopKey: e.hopKey } : undefined
  }

  private syncBuildings(st: GameState): void {
    const seen = new Set<number>()
    for (const b of st.buildings.values()) {
      seen.add(b.id)
      const color = ownerColor(st, b.owner)
      const def = BUILDINGS[b.type]
      // Box orders (chop + terraform) are small ground markers, not structures that
      // rise — render whole (no survey stakes, no clip stages). A floating status badge
      // (hud3d.orderIcons) is their primary indicator; siteBuildTime unused for staging.
      const stage = isBoxOrder(b.type)
        ? 4
        : buildStage(b.done, b.progress, def.buildTime || 1)
      // pile levels quantised so the key (and rebuild) only changes on real steps
      let pileKey = ''
      if (!b.done) {
        for (const res in def.cost) {
          const need = (def.cost as Record<string, number>)[res] ?? 0
          if (need <= 0) continue
          const frac = Math.min(1, ((b.buffer as Record<string, number>)[res] ?? 0) / need)
          pileKey += res[0] + Math.round(frac * 5)
        }
      }
      // A site still in preparation renders as a see-through planned-works overlay
      // (the land below is untouched: trees standing, slopes unlevelled). Recomputed
      // from streamed trees/tiers each frame so the key flips phases as the crew works.
      // Roads keep their normal site marker (they have no model to preview — the
      // geometry fallback would draw a phantom hut); their prep still gates the work.
      const prep = isBoxOrder(b.type) || b.type === 'road' ? null : sitePrepPhase(st, b)
      // Townhalls render a DISPLAY variant: newly crafted rack items fly from
      // the bench first and only mount once the flight lands.
      const sv = b.type === 'plant' ? (b.done ? treeGrowthStage(b, st.tick) : -1)
        : this.rackDisplayVariant(st, b, hallVariant(st, b))
      const key = `${b.type}:${stage}:${color}:${b.dir ?? 0}:${pileKey}:${prep ?? ''}:${sv}:${b.type === 'townhall' ? townHallPhase(b.hallPhase) : 1}`
      let ent = this.bEnts.get(b.id)
      if (!ent || ent.key !== key) {
        if (ent) { this.scene.remove(ent.root); for (const m of ent.mats) m.dispose() }
        ent = { root: this.makeBuilding(st, b, color, stage, prep, sv), key, mats: [] }
        ent.mats = ent.root.userData.mats as Material[]
        this.bEnts.set(b.id, ent)
      }
      const p = hexToPixel(b.q, b.r, S)
      ent.root.position.set(p.x, tileHeight(st, b.q, b.r), p.y)
    }
    for (const [id, ent] of this.bEnts) {
      if (seen.has(id)) continue
      this.scene.remove(ent.root); for (const m of ent.mats) m.dispose()
      this.bEnts.delete(id)
      this.rackDisp.delete(id)
    }
    this.stepRackFlights(st)
  }

  // ---- craft deliveries: bench → weapon rack -------------------------------------
  // When the armory gains an item KIND, the finished piece floats from the
  // workbench to its rack slot; the rack's merged geometry only includes it
  // once the flight lands — no more teleporting onto the rails.
  private rackDisp = new Map<number, number>() // hall id -> displayed rack mask
  private rackFlights: Array<{ hallId: number; item: (typeof RACK_ITEMS)[number]; bit: number; mesh: Mesh; t0: number }> = []

  /** The variant to RENDER for a hall: live store bucket, displayed rack mask. */
  private rackDisplayVariant(st: GameState, b: Building, live: number): number {
    if (b.type !== 'townhall' || !b.done) return live
    const liveMask = live >> 2
    let disp = this.rackDisp.get(b.id)
    if (disp === undefined) {
      // first sight of this hall (or after a reload): show as-is, no theater
      this.rackDisp.set(b.id, liveMask)
      return live
    }
    const removed = disp & ~liveMask
    if (removed) {
      // a serf took gear down — the pickup side has its own equip animation
      disp &= ~removed
      this.rackDisp.set(b.id, disp)
    }
    let added = liveMask & ~disp
    if (added) {
      for (const f of this.rackFlights) if (f.hallId === b.id) added &= ~f.bit
      for (let i = 0; i < RACK_ITEMS.length; i++) {
        if (!(added & (1 << i))) continue
        const mesh = new Mesh(rackItemGeometry(RACK_ITEMS[i]), this.mat)
        mesh.castShadow = true
        this.scene.add(mesh)
        this.rackFlights.push({ hallId: b.id, item: RACK_ITEMS[i], bit: 1 << i, mesh, t0: this.clock })
      }
    }
    return (live & 3) | (disp << 2)
  }

  private stepRackFlights(st: GameState): void {
    const DUR = 0.9
    for (let i = this.rackFlights.length - 1; i >= 0; i--) {
      const f = this.rackFlights[i]
      const hall = st.buildings.get(f.hallId)
      const disp = this.rackDisp.get(f.hallId)
      if (!hall || disp === undefined) {
        this.scene.remove(f.mesh)
        this.rackFlights.splice(i, 1)
        continue
      }
      const tOv = (window as unknown as { RACKT?: number }).RACKT // dev: freeze flights at a fixed t
      const t = typeof tOv === 'number' ? tOv : Math.min(1, (this.clock - f.t0) / DUR)
      const p = hexToPixel(hall.q, hall.r, S)
      const base = tileHeight(st, hall.q, hall.r)
      const slot = rackSlotLocal(disp | f.bit, f.item) ?? { x: 16, y: 21.5, z: 25 }
      // Benchtop → slot. The bench sits on the EAST flank and the rack on the
      // SOUTH wall — a straight line would duck behind the roof corner, so the
      // piece swings out FRONT first (z leads) and arcs high, staying visible
      // the whole way; a light spin settles as it hangs.
      const e = t * t * (3 - 2 * t)
      const zE = Math.sin((Math.PI / 2) * Math.min(1, t * 1.6)) // out in front early
      f.mesh.position.set(
        p.x + HALL_BENCH.x + (slot.x - HALL_BENCH.x) * e,
        base + 11 + (slot.y - 11) * e + Math.sin(Math.PI * t) * 15,
        p.y + (slot.z + 6) * zE - 6 * e, // sail ~6 units proud of the wall, tuck in at the end
      )
      f.mesh.rotation.y = (1 - e) * 2.2
      // fly big, hang small: at rack scale the piece is a speck at gameplay
      // zoom — the showcase size is what makes the delivery readable
      const sc = 1 + (1 - e) * 1.6
      f.mesh.scale.set(sc, sc, sc)
      if (t >= 1) {
        this.scene.remove(f.mesh)
        this.rackFlights.splice(i, 1)
        this.rackDisp.set(f.hallId, disp | f.bit) // lands: the rack mounts it next frame
      }
    }
  }

  /** Build a building's display group: the finished model, or a construction site —
   *  survey stakes, delivered-material piles, and (once work starts) the model
   *  rising bottom-up through clip-plane stages. */
  private makeBuilding(st: GameState, b: (typeof st.buildings extends Map<number, infer B> ? B : never), color: string, stage: number, prep: 'clear' | 'level' | null = null, sv = 0): Group {
    const def = BUILDINGS[b.type]
    const root = new Group()
    root.userData.pick = { kind: 'building', id: b.id }
    root.userData.mats = [] as Material[]
    // pier deck / ramp incline are modelled pointing toward local -Z (planks & rise
    // run down the -Z axis). DIRS[dir] is the target hex (water for a pier, the cliff
    // for a ramp), so we rotate that -Z axis onto DIRS[dir]'s world bearing:
    // θ = dir·60° − 90°. (The old -dir·HEX_RAD both flipped the turn direction and
    // dropped the -90° model offset, so the deck never reached the water.)
    if (b.type === 'pier' || b.type === 'ramp') root.rotation.y = (b.dir ?? 0) * HEX_RAD - Math.PI / 2
    else if (b.type === 'farm') root.rotation.y = (b.dir ?? 0) * HEX_RAD // footprint pair advances +1 in DIRS per step
    else if (buildingFootprint(b.type, b.q, b.r).length > 2) root.rotation.y = 0 // multi-hex plots sit square on their hexes
    else root.rotation.y = (((b.id * 2654435761) >>> 0) % 100) / 100 * 0.3 - 0.15 // subtle jitter

    if (isTerraform(b.type)) {
      // A dig/fill order is not a structure — it leaves the ground bare and lets the
      // floating shovel badge (hud3d.orderIcons) be the sole marker, so the map never
      // grows a little "building" where a serf is only reshaping the earth.
    } else if (prep) {
      // SITE PREP: a see-through image of the planned building floats over the raw
      // land — the trees still stand and the slopes still roll underneath while the
      // crew clears and levels, so nothing about the ground is erased by the click.
      // The solid survey stakes (blueprint) only appear once the plot is ready.
      const m = this.mat.clone()
      m.transparent = true
      m.opacity = 0.34
      m.depthWrite = false
      ;(root.userData.mats as Material[]).push(m)
      const mesh = new Mesh(buildingGeometry(b.type, color, b.id, sv, townHallPhase(b.hallPhase)), m)
      root.add(mesh)
    } else if (stage >= 4) {
      if (b.type === 'farm') {
        const farm = new FarmVisual(b.id, color, this.mat)
        root.userData.farm = farm
        root.add(farm)
      } else {
        const mesh = new Mesh(buildingGeometry(b.type, color, b.id, sv, townHallPhase(b.hallPhase)), this.mat)
        mesh.castShadow = true; mesh.receiveShadow = true
        root.add(mesh)
      }
    } else {
      if (stage === 0) {
        // bare surveyed plot
        root.add(new Mesh(stakesGeometry(), this.mat))
      } else {
        // the model rises bottom-up: clip everything above this stage's height
        const geo = buildingGeometry(b.type, color, b.id, sv, townHallPhase(b.hallPhase))
        if (!geo.boundingBox) geo.computeBoundingBox()
        const h = geo.boundingBox ? geo.boundingBox.max.y : 30
        const cut = tileHeight(st, b.q, b.r) + h * STAGE_H[stage]
        const m = this.mat.clone()
        m.clippingPlanes = [new Plane(new Vector3(0, -1, 0), cut)]
        ;(root.userData.mats as Material[]).push(m)
        const mesh = new Mesh(geo, m)
        mesh.castShadow = true; mesh.receiveShadow = true
        root.add(mesh)
      }
      // delivered materials pile up beside the works and shrink as they're consumed
      for (const res in def.cost) {
        const need = (def.cost as Record<string, number>)[res] ?? 0
        if (need <= 0) continue
        const frac = Math.min(1, ((b.buffer as Record<string, number>)[res] ?? 0) / need)
        if (frac < 0.05) continue
        const slot = PILE_SLOT[res] ?? { x: 0, z: 14 }
        const pile = new Mesh(sitePileGeometry(res), this.mat)
        const sc = 0.45 + 0.75 * frac
        pile.scale.set(sc, sc, sc)
        pile.position.set(slot.x, 0.2, slot.z)
        pile.castShadow = true
        root.add(pile)
      }
    }
    // Multi-hex construction sites (farm, town hall): mark the whole footprint on
    // the ground so its extent reads clearly through the trees still standing on
    // the plot and the faint see-through overlay. Cleared once the building is done.
    if (!b.done && !isBoxOrder(b.type) && !isTerraform(b.type)) {
      const fp = this.footprintPlates(st, b, root.rotation.y)
      if (fp) root.add(fp)
    }
    // Contact-shadow blob grounds tall buildings; the farm lies flat on its hexes
    // (its plots ARE the ground), so a blob just spills a dark ring past the fields.
    // Box-order markers skip it too — a dark disc under them reads as a pit/structure.
    // Prep overlays cast nothing: they're a plan, not a thing standing on the land.
    if (b.type !== 'farm' && !isBoxOrder(b.type) && !prep) {
      const bl = this.blob(buildingFootprint(b.type, b.q, b.r).length > 1 ? 42 : 21)
      bl.position.y = DOME + 0.15 // clear the hex-top crown
      root.add(bl)
    }
    this.scene.add(root)
    return root
  }

  /** Build a fresh entity: an articulated serf Group, or a single-mesh animal. The
   *  serf's legs hang off the root; the upper body + both arms live in an inner group. */
  private makeUnit(id: number, type: string, look: string, color: string): UEnt {
    const base = {
      key: '', yaw: 0, vmp: 0, hopKey: -1, hopQ: NaN, hopR: NaN, lastX: NaN, lastZ: NaN,
      predIdx: 0, predMp: 0, corrX: 0, corrZ: 0, baseX: NaN, baseZ: NaN,
      atkT: 1, lastCd: Infinity, nextSwing: 0, closeF: 0, workF: 0, liftF: 0, swingDur: SWING_DUR, // Infinity: no false trigger on first sight
      slotX: 0, slotZ: 0, sepX: 0, sepZ: 0,
    }
    if (ANIMAL_TYPES.has(type)) {
      const mesh = new Mesh(unitGeometry(type, look, color), this.mat)
      mesh.castShadow = true
      mesh.receiveShadow = true
      mesh.userData.pick = { kind: 'unit', id }
      const blobM = this.blob(9)
      mesh.add(blobM)
      this.scene.add(mesh)
      return { root: mesh, rig: null, blobM, ...base }
    }
    const parts = serfRig(look, color)
    const root = new Group()
    root.userData.pick = { kind: 'unit', id } // one pick tag; the raycast climbs to it
    const legL = new Mesh(parts.leg, this.mat); legL.position.set(-HIP_X, HIP_Y, 0)
    const legR = new Mesh(parts.leg, this.mat); legR.position.set(HIP_X, HIP_Y, 0)
    // Bend at the hips. Core geometry stays in authored feet-space, but every
    // upper-body attachment is placed relative to this waist hinge.
    const inner = new Group(); inner.position.y = HIP_Y
    const core = new Mesh(parts.core, this.mat); core.position.y = -HIP_Y
    const armL = new Mesh(parts.armL, this.mat); armL.position.set(-SHOULDER_X, SHOULDER_Y - HIP_Y, 0)
    const armR = new Mesh(parts.armR, this.mat); armR.position.set(SHOULDER_X, SHOULDER_Y - HIP_Y, 0)
    const held = heldTool(look)
    let tool: Mesh | null = null
    if (held) {
      // gripped in the fist: a posed child of the swinging arm, so every stride,
      // chop and hoe stroke carries the tool with the hand
      const t = new Mesh(held.geo, this.mat)
      t.position.set(HAND.x, HAND.y, HAND.z)
      t.rotation.x = held.rx
      t.castShadow = true
      armR.add(t)
      tool = t
    }
    const carryPivot = new Group(); carryPivot.position.set(0, SHOULDER_Y - HIP_Y, 0)
    inner.add(core, armL, armR, carryPivot)
    root.add(legL, legR, inner)
    const blobM = this.blob(7.5)
    root.add(blobM)
    for (const m of [legL, legR, core, armL, armR]) { m.castShadow = true; m.receiveShadow = true }
    this.scene.add(root)
    // Per-unit phase so a fresh crowd never strides or breathes in lockstep.
    const seed = ((Math.imul(id, 2654435761) >>> 0) / 4294967296) * Math.PI * 2
    const rig: SerfRigInst = {
      inner, legL, legR, armL, armR, carryPivot, tool, toolRest: held?.rx ?? 0, phase: seed, gait: 0, seed, lastMove: -10,
      toolSwing: toolArmScale(look), carry: look === 'carrier', cargo: null, cargoRes: '',
    }
    return { root, rig, blobM, ...base }
  }

  private syncUnits(st: GameState, dt: number): void {
    this.clock += dt
    // crowd slots: units standing on (or headed to) the same hex share it — spread
    // them into a ring so they never render inside each other
    const occ = new Map<string, number[]>()
    for (const u of st.units.values()) {
      const k = u.q + ',' + u.r
      const list = occ.get(k)
      if (list) list.push(u.id); else occ.set(k, [u.id])
    }
    for (const mates of occ.values()) mates.sort((a, b) => a - b)
    // Freeze actual rendered positions for the whole frame. Index by visual
    // position: predicted walkers can be several hexes from their wire position.
    const crowd = new Map<string, Walker[]>()
    for (const u of st.units.values()) {
      const ent = this.uEnts.get(u.id)
      if (!ent || !Number.isFinite(ent.lastX)) continue
      const next = u.wpath?.[0]
      const from = u.mp >= 1 && next ? { q: u.q, r: u.r } : { q: u.fq, r: u.fr }
      const to = u.mp >= 1 && next ? next : u
      const a = hexToPixel(from.q, from.r, S), b = hexToPixel(to.q, to.r, S)
      const dx = b.x - a.x, dz = b.y - a.y, d = Math.hypot(dx, dz)
      const speed = (u.mp < 1 || next) && d > 0 ? HEX_W * hopRate(st, u) : 0
      const walker: Walker = { id: u.id, x: ent.lastX, z: ent.lastZ,
        vx: d ? dx / d * speed : 0, vz: d ? dz / d * speed : 0,
        radius: u.type === 'bear' ? 9 : ANIMAL_TYPES.has(u.type) ? 7 : 6.5 }
      const key = `${Math.floor(walker.x / 64)},${Math.floor(walker.z / 64)}`
      const bucket = crowd.get(key)
      if (bucket) bucket.push(walker); else crowd.set(key, [walker])
    }
    // building bulk per footprint hex, for the walk-around deflection. Farm fields
    // deflect too — but their own farmhands are exempt below, so workers stand in
    // the rows while everyone else skirts the crops.
    const bulk = new Map<string, { x: number; z: number; bid: number; farm: boolean; rad: number }>()
    const farmHex = new Set<string>()
    const sceneryCovered = new Set<string>()
    for (const b of st.buildings.values()) if (b.type !== 'chop' && b.type !== 'plant') {
      for (const f of buildingFootprint(b.type, b.q, b.r, b.dir)) sceneryCovered.add(`${f.q},${f.r}`)
    }
    for (const b of st.buildings.values()) {
      if (WALK_OVER.has(b.type) || isBoxOrder(b.type) || (!b.done && b.progress <= 0)) continue
      const isFarm = b.type === 'farm'
      for (const f of buildingFootprint(b.type, b.q, b.r, b.dir)) {
        // A town hall's footprint is a 7-hex rosette, but only the ANCHOR holds
        // the great hall — the 6 plaza tiles are walkable open ground (serfs
        // cross them, and work the annex / bench / rack that sit on them). Never
        // bulk the plaza: hard-projecting a serf who legitimately walks a plaza
        // tile flips them around the rim as they pass its centre — a blink. The
        // anchor's own radius stays clear of the plaza-tile centres (40 < 42px
        // hex spacing) so the ring is fully free.
        if (b.type === 'townhall' && !(f.q === b.q && f.r === b.r)) continue
        const k = f.q + ',' + f.r
        const p = hexToPixel(f.q, f.r, S)
        const rad = b.type === 'townhall' ? 40 : b.type === 'farm' ? 19 : BULK_RAD
        bulk.set(k, { x: p.x, z: p.y, bid: b.id, farm: isFarm, rad })
        if (isFarm && b.done) farmHex.add(k)
      }
    }
    const seen = new Set<number>()
    for (const u of st.units.values()) {
      seen.add(u.id)
      const color = ownerColor(st, u.owner)
      const look = unitLook(u)
      const key = ANIMAL_TYPES.has(u.type) ? u.type : `c:${look}:${color}`
      let ent = this.uEnts.get(u.id)
      if (!ent || ent.key !== key) {
        // A look/colour change rebuilds the meshes — carry the pose over so the serf
        // doesn't snap to yaw 0 / mid-stride reset when it grabs a tool or a load.
        const prev = ent
        if (ent) this.scene.remove(ent.root)
        ent = this.makeUnit(u.id, u.type, look, color)
        ent.key = key
        if (prev) {
          ent.yaw = prev.yaw; ent.vmp = prev.vmp; ent.hopKey = prev.hopKey
          ent.hopQ = prev.hopQ; ent.hopR = prev.hopR
          ent.lastX = prev.lastX; ent.lastZ = prev.lastZ
          ent.atkT = prev.atkT; ent.lastCd = prev.lastCd; ent.nextSwing = prev.nextSwing
          ent.closeF = prev.closeF; ent.slotX = prev.slotX; ent.slotZ = prev.slotZ
          ent.sepX = prev.sepX; ent.sepZ = prev.sepZ
          ent.predIdx = prev.predIdx; ent.predMp = prev.predMp
          ent.corrX = prev.corrX; ent.corrZ = prev.corrZ
          ent.baseX = prev.baseX; ent.baseZ = prev.baseZ
          if (ent.rig && prev.rig) {
            ent.rig.phase = prev.rig.phase; ent.rig.gait = prev.rig.gait
            ent.rig.lastMove = prev.rig.lastMove
          }
        }
        this.uEnts.set(u.id, ent)
      }
      // Smooth movement: the sim ticks at 1 Hz, so u.mp only steps forward once a
      // second. Advance a per-unit visual progress by wall-clock time toward the hop's
      // end (units always finish the current hop server-side, so this can't overshoot),
      // never dropping below the authoritative mp. A changed hop (fq,fr,q,r) — a new
      // step, a reroute — resnaps to the server value so we stay honest.
      const hopKey = (Math.imul(u.fq | 0, 73856093) ^ Math.imul(u.fr | 0, 19349663)
        ^ Math.imul(u.q | 0, 83492791) ^ Math.imul(u.r | 0, 40503619)) >>> 0
      const resnapped = ent.hopKey !== hopKey
      const sMp = u.mp < 0 ? 0 : u.mp > 1 ? 1 : u.mp
      if (resnapped) {
        // Path continuation (the new hop sets off from the old hop's destination):
        // carry the visual position over exactly — the leftover distance becomes a
        // small negative start on the new segment, and the catch-up below reels it
        // in. A hard snap here teleported ~a tenth of a hex at every hop boundary.
        const cont = ent.hopQ === u.fq && ent.hopR === u.fr && ent.vmp > 0.5 && ent.vmp <= 1
        ent.vmp = cont ? Math.max(ent.vmp - 1, sMp - 0.35) : sMp
        ent.hopKey = hopKey; ent.hopQ = u.q; ent.hopR = u.r
      } else {
        const rate = hopRate(st, u) * dt
        const lead = ent.vmp - sMp
        if (lead >= 0) {
          // Ahead of the server: damp by the lead so any rate mismatch self-balances
          // into a gentle speed wobble instead of slamming into the mp=1 clamp.
          const damp = Math.min(1, Math.max(0.12, 1 - (lead - 0.05) * 2.2))
          ent.vmp = Math.min(1, ent.vmp + rate * damp)
        } else {
          // Behind (fresh continuation / server tick landed): full rate plus a soft
          // proportional pull — converges in ~0.3 s with no teleport.
          ent.vmp = Math.min(1, ent.vmp + rate + (sMp - ent.vmp) * Math.min(1, dt * 5))
        }
      }
      const mp = ent.vmp

      // ---- Dead reckoning: the wire carries only the CURRENT hop, so a lag gap
      // used to freeze walkers at the hex edge (vmp clamps at 1). Own units also
      // carry their route (wpath, next steps first) — when the hop is spent and
      // no fresh tick has landed, keep walking the known path locally (bounded
      // by PRED_MAX_HOPS), and let the correction glide below absorb whatever
      // the server eventually disagrees on.
      if (resnapped) { ent.predIdx = 0; ent.predMp = 0 }
      const wp = u.wpath
      if (ent.vmp >= 1 && wp && wp.length > ent.predIdx) {
        const dest = wp[ent.predIdx]
        // Never render a walker entering a full hex while the server queues it.
        const occupied = (occ.get(`${dest.q},${dest.r}`) ?? []).filter(id => id !== u.id).length
        if (occupied < HEX_CAP) ent.predMp = Math.min(1, ent.predMp + hopRateAt(st, u, dest.q, dest.r) * dt)
        if (ent.predMp >= 1 && ent.predIdx + 1 < Math.min(wp.length, PRED_MAX_HOPS)) {
          ent.predIdx++
          ent.predMp = 0
        }
      }
      const predicting = wp !== undefined && wp.length > ent.predIdx && (ent.predIdx > 0 || ent.predMp > 0)
      const from = predicting ? (ent.predIdx === 0 ? { q: u.q, r: u.r } : wp![ent.predIdx - 1]) : { q: u.fq, r: u.fr }
      const to = predicting ? wp![ent.predIdx] : { q: u.q, r: u.r }
      const useMp = predicting ? ent.predMp : mp
      const cmp = useMp < 0 ? 0 : useMp // clamped for height/overlays; raw mp may run negative briefly

      const a = hexToPixel(from.q, from.r, S), bpx = hexToPixel(to.q, to.r, S)
      let wx = lerp(a.x, bpx.x, useMp), wz = lerp(a.y, bpx.y, useMp)
      let y = lerp(tileHeight(st, from.q, from.r), tileHeight(st, to.q, to.r), cmp)

      // The hall's crafter stands AT the side workbench baked into the
      // townhall model (HALL_BENCH offset), facing it — not wherever the
      // plaza crowd-flow left them. Applied BEFORE the correction glide so
      // the step over from the arrival hex eases instead of popping.
      let benched = false
      if (u.state === 'working' && u.job !== undefined) {
        const jb = st.buildings.get(u.job)
        if (jb && jb.type === 'townhall') {
          const hp = hexToPixel(jb.q, jb.r, S)
          wx = hp.x + HALL_BENCH.x
          wz = hp.y + HALL_BENCH.z
          y = tileHeight(st, jb.q, jb.r)
          benched = true
        }
      }

      // ---- Correction glide: a late tick (or a prediction miss) can land the
      // authority a hop or two from where we were rendering. Absorb the jump
      // into an offset that decays in ~200 ms so the unit glides to the truth;
      // big jumps (respawns, hall exits) still snap outright.
      //
      // The floor matters: on smooth-interpolation frames the per-frame advance
      // must NOT be absorbed (that would accumulate into steady rubber-banding),
      // so it stays at CORR_MIN. But a resnap frame (a hop boundary or a server
      // repath, ~once/sec) carries a DISCRETE reposition — often a small one
      // (0.05–0.3 hex) that used to snap through the floor and read as a flicker,
      // worst around build sites where serfs start, stop and turn. On those
      // frames drop the floor so even small discrete jumps glide.
      if (Number.isFinite(ent.baseX)) {
        const jx = ent.baseX - wx, jz = ent.baseZ - wz
        const jump = Math.hypot(jx, jz)
        const floor = resnapped ? HEX_W * 0.02 : CORR_MIN
        if (jump > floor && jump < CORR_MAX) {
          ent.corrX += jx
          ent.corrZ += jz
          const cm = Math.hypot(ent.corrX, ent.corrZ)
          if (cm > CORR_MAX) { ent.corrX *= CORR_MAX / cm; ent.corrZ *= CORR_MAX / cm }
        }
      }
      ent.baseX = wx; ent.baseZ = wz
      const cf = Math.min(1, dt * CORR_EASE)
      ent.corrX -= ent.corrX * cf
      ent.corrZ -= ent.corrZ * cf
      wx += ent.corrX; wz += ent.corrZ

      // Moving people use consistent right-hand lanes. Resting groups use a
      // fixed-orientation ring; changing hexes no longer spins the entire group.
      const mates = occ.get(u.q + ',' + u.r)
      let sx = 0, sz = 0
      const dx = bpx.x - a.x, dz = bpx.y - a.y, travel = Math.hypot(dx, dz)
      const walking = travel > 0 && (useMp < 1 || !!u.wpath?.length)
      if (walking) { sx = -dz / travel * 6.5; sz = dx / travel * 6.5 }
      else if (mates && mates.length > 1) {
        const ang = (mates.indexOf(u.id) / mates.length) * Math.PI * 2
        const rad = Math.min(14, 5.5 * Math.sqrt(mates.length))
        sx = Math.cos(ang) * rad; sz = Math.sin(ang) * rad
      }
      ent.slotX += (sx - ent.slotX) * Math.min(1, dt * SLOT_EASE)
      ent.slotZ += (sz - ent.slotZ) * Math.min(1, dt * SLOT_EASE)
      if (!benched) { wx += ent.slotX; wz += ent.slotZ }

      const nearby: Walker[] = []
      const cx = Math.floor(wx / 64), cz = Math.floor(wz / 64)
      for (let x = cx - 1; x <= cx + 1; x++) for (let z = cz - 1; z <= cz + 1; z++) {
        nearby.push(...(crowd.get(`${x},${z}`) ?? []))
      }
      const speed = walking ? HEX_W * hopRate(st, u) : 0
      const push = separation({ id: u.id, x: wx, z: wz,
        vx: travel ? dx / travel * speed : 0, vz: travel ? dz / travel * speed : 0,
        radius: u.type === 'bear' ? 9 : ANIMAL_TYPES.has(u.type) ? 7 : 6.5 }, nearby)
      const ease = 1 - Math.exp(-dt * AVOID_EASE)
      ent.sepX += (push.x - ent.sepX) * ease
      ent.sepZ += (push.z - ent.sepZ) * ease
      if (!benched) { wx += ent.sepX; wz += ent.sepZ }

      const obstacles: Obstacle[] = []
      // Include both ends of the rendered hop, including predicted hops.
      const checked = new Set<string>()
      for (const center of [from, to, u]) for (let e = -1; e < 6; e++) {
        const nq = center.q + (e < 0 ? 0 : DIRS[e].q), nr = center.r + (e < 0 ? 0 : DIRS[e].r)
        const k = `${nq},${nr}`
        if (checked.has(k)) continue
        checked.add(k)
        const bb = bulk.get(k)
        if (bb) {
          if (u.job === bb.bid && (u.state === 'building' || u.state === 'working')) continue
          obstacles.push({ x: bb.x, z: bb.z, radius: bb.rad })
        } else if (!sceneryCovered.has(k)) {
          const chunk = st.chunks.get(chunkKey(Math.floor(nq / CHUNK), Math.floor(nr / CHUNK)))
          const index = ((nr % CHUNK + CHUNK) % CHUNK) * CHUNK + (nq % CHUNK + CHUNK) % CHUNK
          if (chunk && !chunk.roads.has(index) && (chunk.trees?.[index] === 1 || chunk.tiles[index] === Terrain.Forest)) {
            // Tree stands scatter around the crown. Keep walkers outside the
            // trunks; a felled stand disappears with its normal chunk update.
            const p = hexToPixel(nq, nr, S)
            obstacles.push({ x: p.x, z: p.y, radius: 16 })
          }
        }
      }

      // resolve the attack target once — it drives melee closing, swings and facing.
      // state 'fighting' covers the whole engagement INCLUDING the chase, so anything
      // that should only happen when blows can land is gated by actual hex range.
      const tgt = u.state === 'fighting' && u.target
        ? (u.target.kind === 'u' ? st.units.get(u.target.id) : st.buildings.get(u.target.id))
        : undefined
      const tp = tgt ? hexToPixel(tgt.q, tgt.r, S) : null
      const combat = tgt ? unitCombat(u) : null
      const tDist = tgt ? hexDist(u.q, u.r, tgt.q, tgt.r) : Infinity
      const inRange = combat !== null && tDist <= Math.max(1, combat.range)

      // melee closing: step visually up to the victim instead of striking across the
      // hex gap; the displacement feeds the stride below, so they walk into the fight
      const wantClose = tp !== null && combat !== null && combat.range <= 1 && tDist <= 1
      ent.closeF += ((wantClose ? 1 : 0) - ent.closeF) * Math.min(1, dt * (wantClose ? CLOSE_RISE : CLOSE_FALL))
      if (ent.closeF > 0.01 && tp) {
        const tdx = tp.x - wx, tdz = tp.y - wz
        const d = Math.hypot(tdx, tdz)
        if (d > 1) {
          const stand = Math.max(CLOSE_MIN, d / 2 + CLOSE_MARGIN)
          const off = Math.min(Math.max(0, d - stand), CLOSE_MAX) * ent.closeF
          wx += (tdx / d) * off; wz += (tdz / d) * off
        }
      }

      if (!benched && Number.isFinite(ent.lastX)) {
        const previous = { x: ent.lastX, z: ent.lastZ }
        const gap = Math.hypot(wx - previous.x, wz - previous.z)
        // Keep late packets and wall crossings within a walking stride. Large
        // world changes still snap so respawns do not walk across the map.
        if (gap < CORR_MAX) {
          const budget = Math.max(18, HEX_W * hopRate(st, u) * 1.6) * Math.min(dt, 0.1)
          const pos = avoidObstacles(previous, { x: wx, z: wz }, budget, obstacles)
          wx = pos.x; wz = pos.z
        }
      }

      // Field work: a farmhand standing in the rows stands ON the tilled-soil
      // platform (not knee-deep in it) — the lift eases in with the working pose
      // and is part of y so rings, bars and name tags ride along. Builders and
      // diggers ('building' state) share the bowed laboring pose and swing, but
      // only farmhands ride the soil lift.
      const farming = u.type === 'citizen' && u.state === 'working' && farmHex.has(u.q + ',' + u.r)
      // Crafters — the hall's bench, the workbench, the smithy anvil — share
      // the builder's bent posture and stroke, but only while an item order is
      // actually on the queue (a crafter waiting on materials stands easy).
      let crafting = false
      if (u.type === 'citizen' && u.state === 'working' && u.job !== undefined) {
        const jb = st.buildings.get(u.job)
        crafting =
          !!jb &&
          (jb.type === 'townhall' || jb.type === 'workbench' || jb.type === 'barracks') &&
          jb.queue.some((o) => o.item != null)
      }
      const laboring = farming || crafting || (u.type === 'citizen' && u.state === 'building')
      // Bench crafters lean HALF as deep: their work sits at waist height, so
      // the farmhand's full ground-bow rams them face-first into the benchtop.
      ent.workF += ((laboring ? (crafting ? 0.42 : 1) : 0) - ent.workF) * Math.min(1, dt * 3)
      ent.liftF += ((farming ? 1 : 0) - ent.liftF) * Math.min(1, dt * 3)
      y += FARM_SOIL_LIFT * ent.liftF

      // World distance moved this frame drives the gait envelope and the stride phase
      // (phase per distance → feet plant, never skate). Zero it on a hop resnap or the
      // first sighting so a reroute/teleport can't pop the phase.
      const mdx = wx - ent.lastX, mdz = wz - ent.lastZ
      const moved = resnapped || !Number.isFinite(ent.lastX) ? 0 : Math.hypot(wx - ent.lastX, wz - ent.lastZ)
      ent.lastX = wx; ent.lastZ = wz
      this.unitAnim.set(u.id, { x: wx, y, z: wz })
      // Publish the on-screen position like the 2.5D renderer does (same
      // hexToPixel plane) — shared consumers and probes read what's rendered.
      st.unitPos.set(u.id, { x: wx, y: wz })

      // Strike trigger: a rising attack cooldown marks a blow for slow weapons, but
      // at the common 1-tick cadence it re-arms invisibly between snapshots — so a
      // fighting unit also swings on its own rhythm at its true attack period.
      if (u.atkCd > ent.lastCd) { ent.atkT = 0; ent.swingDur = SWING_DUR; ent.nextSwing = this.clock + Math.max(1, unitCombat(u).period) }
      ent.lastCd = u.atkCd
      if (inRange && combat !== null && this.clock >= ent.nextSwing) {
        ent.atkT = 0
        ent.swingDur = SWING_DUR
        ent.nextSwing = this.clock + Math.max(1, combat.period)
      }
      // Labor rhythm: farmhands hoe, builders hammer, diggers shovel — a slow
      // steady stroke (per-serf offset so a crew doesn't swing in lockstep).
      if (laboring && this.clock >= ent.nextSwing) {
        ent.atkT = 0
        ent.swingDur = FARM_SWING_DUR
        ent.nextSwing = this.clock + FARM_SWING_EVERY + ((u.id % 7) / 7) * 0.8
      }
      if (ent.atkT < 1) ent.atkT = Math.min(1, ent.atkT + dt / ent.swingDur)
      const pulse = ent.atkT < 1 ? strikePulse(ent.atkT) : 0

      // Face actual travel after avoidance, even when each frame moves only a
      // fraction of a world unit. Use speed to filter idle drift consistently
      // across frame rates; a stopped unit keeps its heading instead of turning
      // back toward the old hop. Only face a combat target once in attack range.
      const facingMove = moved > FACE_SPEED_EPS * dt
      let fx = facingMove ? mdx : walking ? bpx.x - a.x : 0
      let fz = facingMove ? mdz : walking ? bpx.y - a.y : 0
      if (tp && inRange) { fx = tp.x - wx; fz = tp.y - wz }
      if (benched) { fx = 0; fz = -1 } // square up to the benchtop
      if (fx * fx + fz * fz > 1e-8) {
        // pivot toward the facing at a constant rate — an exponential ease
        // front-loads the turn so hard it reads as an instant spin
        const target = Math.atan2(-fx, -fz)
        let d = target - ent.yaw
        d = Math.atan2(Math.sin(d), Math.cos(d)) // shortest signed turn
        const maxStep = TURN_SPEED * dt
        ent.yaw += Math.abs(d) <= maxStep ? d : Math.sign(d) * maxStep
      }
      ent.root.position.set(wx, y, wz)
      ent.root.rotation.y = ent.yaw
      // contact blob rides the crowned hex top so it never sinks into the dome
      ent.blobM.position.y = 0.14 + crownAt(cmp)
      if (ent.rig) {
        if (moved > MOVE_EPS) ent.rig.lastMove = this.clock
        this.walk(ent.rig, moved, dt)
        if (ent.rig.carry) this.syncCargo(ent.rig, dominantCargo(u))
        const rig = ent.rig
        // Work tools use a forward lift and a short downward stroke. The old
        // combat backswing flung the sleeve behind the shoulder and flipped the hoe.
        const work = inRange ? 0 : ent.workF
        const t = laboring ? Math.min(1, ent.atkT) : 1
        const smooth = (v: number): number => { const x = Math.max(0, Math.min(1, v)); return x * x * (3 - 2 * x) }
        const lift = t < .35 ? smooth(t / .35) : 1 - smooth((t - .35) / .35)
        if (!laboring && ent.atkT < 1) rig.armR.rotation.x += strikeArm(ent.atkT) * (1 - work)
        rig.inner.rotation.x -= .14 * pulse * (1 - work) + (.2 + .04 * (1 - lift)) * work
        rig.inner.position.z = 0 // the waist stays over the planted legs
        rig.armR.rotation.x = lerp(rig.armR.rotation.x, .45 + .45 * lift, work)
        rig.armL.rotation.x = lerp(rig.armL.rotation.x, .18 + .12 * lift, work)
        if (rig.tool) {
          // Compensate for the torso and shoulder angles at the wrist, keeping
          // the working end ahead of the hands and clear of the field surface.
          const shaft = -1.83 + .53 * lift
          rig.tool.rotation.x = lerp(rig.toolRest, shaft - rig.inner.rotation.x - rig.armR.rotation.x, work)
        }
      } else {
        // animal bite: whole-body lunge toward the facing with a nose-down snap
        ent.root.rotation.x = -0.3 * pulse
        if (pulse > 0) {
          ent.root.position.x = wx - Math.sin(ent.yaw) * 4.5 * pulse
          ent.root.position.z = wz - Math.cos(ent.yaw) * 4.5 * pulse
        }
      }
    }
    for (const [id, ent] of this.uEnts) {
      if (seen.has(id)) continue
      this.scene.remove(ent.root); this.uEnts.delete(id); this.unitAnim.delete(id)
      st.unitPos.delete(id)
    }
  }

  /** Drive one serf's walk from the distance it moved this frame. gait (0..1) fades the
   *  whole cycle in when walking and out when it stops, so limbs settle to a stand and a
   *  faint idle breath fills the neutral pose. Legs (on the root) swing at the hips; the
   *  inner upper body dips into the hips (down-only → no waist gap), leans and sways. */
  private walk(rig: SerfRigInst, moved: number, dt: number): void {
    // Grace window: a hop resnap forces one zero-moved frame, and short server-side
    // pauses (capacity holds, job think ticks) shouldn't flick the limbs to neutral.
    const goal = this.clock - rig.lastMove < GAIT_GRACE ? 1 : 0
    rig.gait += (goal - rig.gait) * Math.min(1, dt * (goal > rig.gait ? GAIT_RISE : GAIT_FALL))
    rig.phase += moved * STRIDE_FREQ
    const g = rig.gait
    const s = Math.sin(rig.phase)
    rig.legL.rotation.x = s * LEG_SWING * g
    rig.legR.rotation.x = -s * LEG_SWING * g
    if (rig.carry) {
      // both arms cradle the load, bouncing gently twice per stride; the cargo hangs
      // off the same shoulder hinge, so it moves exactly with the hands
      const pitch = CARRY_ARM + Math.sin(2 * rig.phase) * 0.09 * g
      rig.armL.rotation.x = pitch
      rig.armR.rotation.x = pitch
      rig.carryPivot.rotation.x = pitch - CARRY_ARM
    } else {
      rig.armL.rotation.x = -s * ARM_SWING * g // free arm, opposite the left leg
      rig.armR.rotation.x = s * ARM_SWING * rig.toolSwing * g // tool arm, gated by weight
    }
    // upper body dips at double-support (down-only, into the hips), leans forward, sways;
    // + a slow breathing rise that cross-fades in only while standing (1 - gait).
    const bob = -(1 - Math.cos(2 * rig.phase)) * 0.5 * BOB * g
    const breath = (1 - g) * IDLE_BOB * Math.sin(this.clock * IDLE_FREQ + rig.seed)
    rig.inner.position.y = HIP_Y + bob + breath
    rig.inner.rotation.x = -LEAN * g
    rig.inner.rotation.z = s * SWAY * g
  }

  /** Swap a hauler's held cargo model to match the resource it carries the most of. */
  private syncCargo(rig: SerfRigInst, res: string): void {
    if (res === rig.cargoRes) return
    rig.cargoRes = res
    if (rig.cargo) { rig.carryPivot.remove(rig.cargo); rig.cargo = null }
    if (res) {
      // No pick tag needed — the raycast climbs parents to the root's tag. Lives in
      // the shoulder-hinged carryPivot (geometry is feet-space, so offset back down)
      // so the load swings exactly with the carrying arms.
      const m = new Mesh(cargoGeometry(res), this.mat)
      m.castShadow = true
      m.position.y = -SHOULDER_Y
      rig.carryPivot.add(m)
      rig.cargo = m
    }
  }
}
